import * as SwitchPrimitives from "@radix-ui/react-switch";
import { useId } from "react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { cn } from "../../utils/cn";

import { INSTALL_FOR_PUSH, unblockInstructions } from "./push-platform";
import {
  TURN_ON_FAILED,
  usePushRegistration,
  type PushDeviceState,
} from "./use-push-registration";

/** What the explanation under the switch says, in every state. */
export const PUSH_EXPLAINER =
  "Push is normally on once you register for an event: your browser asks you then, once. Announcements from all your events then arrive on this device, even when the app is closed. To stop them here, turn this switch off. The bell in the app keeps every announcement either way.";

const STATUS: Record<Exclude<PushDeviceState, "unavailable">, string> = {
  checking: "Checking what this device supports…",
  unsupported: "This browser cannot show push notifications.",
  install: "On iPhone and iPad, push works only from the Home Screen app.",
  available:
    "Not on yet for this device. Turn the switch on to get announcements here.",
  enabling: "Turning on…",
  enabled: "On for this device.",
  failed: TURN_ON_FAILED,
  disabling: "Turning off…",
  off: "Off on this device.",
  blocked: "Notifications are blocked in this browser.",
};

/**
 * This device's push notifications, with the switch that turns them on and
 * off. The same control sits in the inbox sheet and on the Profile page, so
 * both read and change the one registration the router root mounts.
 *
 * Push normally comes on without this switch: the "Register for this
 * event" click asks for the permission and registers the device. The switch
 * is how a person changes their mind, and how somebody who registered before
 * that ask existed, or closed the prompt, turns push on. On asks for the
 * permission if the browser has not been asked yet, and registers the
 * device. Off removes this device's token so the backend stops
 * sending to it, and leaves the browser permission alone. Where the switch
 * cannot do anything (blocked, iPhone browser tab, unsupported browser) it
 * stays visible but disabled, next to the reason.
 *
 * Renders nothing where push is not configured for the build (production
 * today) or outside a provider, as every other push surface does.
 */
export function PushSettings({
  testId,
  className,
}: {
  testId: string;
  className?: string;
}) {
  const { device, error, enable, disable } = usePushRegistration();
  const id = useId();

  if (device === "unavailable") return null;

  const checked = device === "enabled" || device === "enabling";
  const actionable =
    device === "available" ||
    device === "enabled" ||
    device === "off" ||
    device === "failed";
  const switchId = `${id}-switch`;
  const statusId = `${id}-status`;
  const explainerId = `${id}-explainer`;

  return (
    <div
      data-testid={testId}
      data-state={device}
      className={cn("min-w-0 space-y-1.5 text-sm", className)}
    >
      <div className="flex items-center justify-between gap-3">
        <label
          htmlFor={switchId}
          className={cn(
            "min-w-0 flex-1 font-medium text-foreground",
            actionable ? "cursor-pointer" : "cursor-default",
          )}
        >
          Push notifications on this device
        </label>
        <SwitchPrimitives.Root
          id={switchId}
          checked={checked}
          disabled={!actionable}
          onCheckedChange={(on) => (on ? enable() : disable())}
          aria-describedby={`${statusId} ${explainerId}`}
          data-testid={`${testId}-switch`}
          // The whole 44px box is the target; the track drawn inside it is
          // the usual size. -my keeps the row from growing on a desktop.
          className="group -my-2.5 inline-flex h-11 w-14 shrink-0 cursor-pointer items-center justify-center rounded-full focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed"
        >
          <span className="flex h-6 w-11 items-center rounded-full border-2 border-transparent bg-input transition-colors group-disabled:opacity-50 group-data-[state=checked]:bg-hb-purple-fill dark:group-data-[state=checked]:border-lit-edge">
            <SwitchPrimitives.Thumb className="pointer-events-none block h-5 w-5 rounded-full bg-white shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0" />
          </span>
        </SwitchPrimitives.Root>
      </div>
      {/* Always mounted, so a screen reader hears each change of state. */}
      <p
        id={statusId}
        role="status"
        aria-live="polite"
        data-testid={`${testId}-text`}
        className={cn(
          "wrap-anywhere",
          device === "blocked"
            ? "font-medium text-foreground"
            : "text-muted-foreground",
        )}
      >
        {error ?? STATUS[device]}
      </p>
      {device === "blocked" && (
        <p className="wrap-anywhere text-muted-foreground">
          {unblockInstructions()}
        </p>
      )}
      {device === "install" && (
        <p className="wrap-anywhere text-muted-foreground">
          {INSTALL_FOR_PUSH}
        </p>
      )}
      <p
        id={explainerId}
        className="wrap-anywhere text-xs text-muted-foreground"
      >
        {PUSH_EXPLAINER}
      </p>
    </div>
  );
}

/**
 * The push row inside the inbox sheet: where a person turns push off, turns
 * it on if the prompt at registration was closed, or finds out why nothing
 * arrives after blocking it. Only visible once the sheet is open, so it never
 * needs dismissing itself.
 */
export function PushStatus() {
  return (
    <PushSettings
      testId="push-status"
      className="rounded-md border border-border bg-muted px-3 py-2.5"
    />
  );
}

/**
 * The Notifications section of the Profile page: the account-wide home of
 * the push switch. Absent, heading included, wherever push is not configured.
 */
export function NotificationSettingsCard({
  className,
}: {
  className?: string;
}) {
  const { device } = usePushRegistration();
  if (device === "unavailable") return null;
  return (
    <Card className={className} data-testid="profile-notifications">
      <CardHeader>
        <CardTitle>Notifications</CardTitle>
      </CardHeader>
      <CardContent>
        <PushSettings testId="profile-push" />
      </CardContent>
    </Card>
  );
}
